import { useState } from 'react';
import { useI18n } from '../hooks/use-i18n';

function DangerZone({ deleteAccount }) {
  const { t } = useI18n();
  const [confirmed, setConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (!confirmed || deleting) return;
    setDeleting(true);
    await deleteAccount?.();
  }

  return (
    <div style={{ maxWidth: 480 }}>
      <div style={{ color: '#c46050', fontSize: 18, fontWeight: 'bold', marginBottom: 6, fontFamily: 'Georgia, serif' }}>
        Delete account
      </div>
      <div style={{ color: '#444', fontSize: 13, fontStyle: 'italic', lineHeight: 1.7, marginBottom: 20, fontFamily: 'Georgia, serif' }}>
        Everything Shift knows about you will be permanently deleted. This cannot be undone.
      </div>
      <div style={{ height: '0.5px', background: '#1e1e2a', marginBottom: 16 }} />
      <div style={{ color: '#2a2a2a', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10, fontFamily: 'Georgia, serif' }}>
        What gets deleted
      </div>
      {['All sessions and messages', 'Your portrait and patterns', 'All artifacts and versions', 'Your playbook, notes, and signals', 'Your account and profile'].map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '0.5px solid #111' }}>
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#c46050', opacity: 0.5, flexShrink: 0 }} />
          <span style={{ color: '#555', fontSize: 13, fontFamily: 'Georgia, serif' }}>{item}</span>
        </div>
      ))}
      <div style={{ height: '0.5px', background: '#1e1e2a', margin: '20px 0' }} />
      <div
        onClick={() => setConfirmed(p => !p)}
        style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20, cursor: 'pointer' }}
      >
        <div style={{
          width: 16, height: 16, border: `1px solid ${confirmed ? '#c46050' : '#333'}`,
          borderRadius: 3, flexShrink: 0, marginTop: 1,
          background: confirmed ? '#c46050' : 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {confirmed && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 2.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
        </div>
        <span style={{ color: '#555', fontSize: 13, lineHeight: 1.6, fontFamily: 'Georgia, serif' }}>
          I understand this is permanent and cannot be undone
        </span>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          style={{ flex: 1, background: 'none', border: '1px solid #2a2a2a', borderRadius: 8, color: '#555', fontSize: 13, fontFamily: 'Georgia, serif', padding: 10, cursor: 'pointer' }}
        >
          Cancel
        </button>
        <button
          onClick={handleDelete}
          disabled={!confirmed}
          style={{
            flex: 1, background: 'none',
            border: `1px solid ${confirmed ? '#c4605044' : '#272736'}`,
            borderRadius: 8,
            color: confirmed ? '#c46050' : '#333',
            fontSize: 13, fontFamily: 'Georgia, serif', padding: 10,
            cursor: confirmed ? 'pointer' : 'default',
            opacity: deleting ? 0.5 : 1,
          }}
        >
          {deleting ? 'Deleting...' : 'Delete my account'}
        </button>
      </div>
      <div style={{ color: '#2a2a2a', fontSize: 11, fontStyle: 'italic', textAlign: 'center', marginTop: 16, fontFamily: 'Georgia, serif' }}>
        You have 30 days to log back in and cancel before deletion runs.
      </div>
    </div>
  );
}

export default function SettingsPanel({
  clearHistory,
  clearIdeas,
  clearPortrait,
  deleteAccount,
  exportData,
  nameEdit,
  saveAiName,
  savePreferences,
  saveUsernameFromSettings,
  setNameEdit,
  setSettingsFirstName,
  setSettingsLastName,
  setSettingsTab,
  setUserRecord,
  setUsernameInput,
  settingsFirstName,
  settingsLastName,
  settingsSaving,
  settingsTab,
  userRecord,
  usernameInput,
  session,
  supabase,
  onClose,
}) {
  const [clearing, setClearing] = useState(null);

  return (
    <div style={{
      width: '100%', height: '100%',
      background: '#07070a', display: 'flex',
      flexDirection: 'column', fontFamily: 'Georgia, serif',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 28px', borderBottom: '0.5px solid #1e1e2a', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 8,
            background: '#272736', border: '1px solid #2a2a2a',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, color: '#888',
          }}>⚙</div>
          <div>
            <div style={{ color: '#e0e0e0', fontSize: 20, fontWeight: 'normal' }}>Settings</div>
          </div>
          <div style={{ color: '#555', fontSize: 13, marginLeft: 10 }}>account · ai · privacy</div>
        </div>
        <div
          onClick={() => onClose?.()}
          style={{
            width: 28, height: 28, borderRadius: '50%', border: '1px solid #2a2a2a',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#555',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#888'; e.currentTarget.style.borderColor = '#444'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#555'; e.currentTarget.style.borderColor = '#2a2a2a'; }}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </div>
      </div>

      {/* Tab nav */}
      <div style={{
        display: 'flex', justifyContent: 'center', gap: 36,
        borderBottom: '0.5px solid #1e1e2a', flexShrink: 0,
      }}>
        {[
          { id: 'account', label: 'Account' },
          { id: 'ai', label: 'AI' },
          { id: 'privacy', label: 'Privacy' },
          { id: 'session', label: 'Session' },
          { id: 'danger', label: 'Danger zone' },
        ].map(tab => (
          <div
            key={tab.id}
            onClick={() => setSettingsTab(tab.id)}
            style={{
              padding: '12px 4px 14px', cursor: 'pointer',
              color: settingsTab === tab.id ? (tab.id === 'danger' ? '#c46050' : '#c8a96e') : (tab.id === 'danger' ? '#c4605088' : '#555'),
              borderBottom: `1.5px solid ${settingsTab === tab.id ? (tab.id === 'danger' ? '#c46050' : '#c8a96e') : 'transparent'}`,
              fontSize: 14,
            }}
            onMouseEnter={e => { if (settingsTab !== tab.id) e.currentTarget.style.color = tab.id === 'danger' ? '#c46050' : '#888'; }}
            onMouseLeave={e => { if (settingsTab !== tab.id) e.currentTarget.style.color = tab.id === 'danger' ? '#c4605088' : '#555'; }}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px', maxWidth: 680 }}>

          {settingsTab === 'ai' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
              <div>
                <div style={{ color: '#777', fontSize: '12px', letterSpacing: '0.1em', marginBottom: '10px' }}>AI name</div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input value={nameEdit} onChange={e => setNameEdit(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveAiName(nameEdit)}
                    style={{ flex: 1, background: 'transparent', border: 'none', borderBottom: '1px solid #333', outline: 'none', color: '#d4d4d4', fontSize: '15px', fontFamily: 'Georgia, serif', padding: '4px 0' }} />
                  <button onClick={() => saveAiName(nameEdit)} style={{ background: 'none', border: '1px solid #333', borderRadius: '3px', color: '#888', fontSize: '12px', letterSpacing: '0.08em', cursor: 'pointer', fontFamily: 'Georgia, serif', padding: '6px 14px' }}>save</button>
                </div>
              </div>
              <div>
                <div style={{ color: '#777', fontSize: '12px', letterSpacing: '0.1em', marginBottom: '12px' }}>Response length</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[{ val: 'brief', label: 'Brief' }, { val: 'balanced', label: 'Balanced' }, { val: 'thorough', label: 'Thorough' }].map(o => {
                    const prefs = userRecord?.preferences || {};
                    return (
                      <button key={o.val} onClick={() => savePreferences({ response_length: o.val })}
                        style={{ background: 'none', border: `1px solid ${(prefs.response_length || 'balanced') === o.val ? '#888' : '#333'}`, borderRadius: '3px', color: (prefs.response_length || 'balanced') === o.val ? '#d4d4d4' : '#555', fontSize: '13px', padding: '7px 14px', cursor: 'pointer', fontFamily: 'Georgia, serif' }}>
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div style={{ color: '#777', fontSize: '12px', letterSpacing: '0.1em', marginBottom: '12px' }}>Depth mode</div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[{ val: 'practical', label: 'Practical only' }, { val: 'open', label: 'Open to depth' }, { val: 'depth_welcome', label: 'Depth welcome' }].map(o => {
                    const prefs = userRecord?.preferences || {};
                    return (
                      <button key={o.val} onClick={() => savePreferences({ depth_mode: o.val })}
                        style={{ background: 'none', border: `1px solid ${(prefs.depth_mode || 'open') === o.val ? '#888' : '#333'}`, borderRadius: '3px', color: (prefs.depth_mode || 'open') === o.val ? '#d4d4d4' : '#555', fontSize: '13px', padding: '7px 14px', cursor: 'pointer', fontFamily: 'Georgia, serif' }}>
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {settingsSaving && <div style={{ color: '#555', fontSize: '12px', letterSpacing: '0.08em' }}>saving...</div>}
            </div>
          )}

          {settingsTab === 'session' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
              <div>
                <div style={{ color: '#777', fontSize: '12px', letterSpacing: '0.1em', marginBottom: '12px' }}>Break reminders</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[{ val: 'on', label: 'On' }, { val: 'off', label: 'Off' }].map(o => {
                    const prefs = userRecord?.preferences || {};
                    return (
                      <button key={o.val} onClick={() => savePreferences({ break_timer: o.val })}
                        style={{ background: 'none', border: `1px solid ${(prefs.break_timer || 'on') === o.val ? '#888' : '#333'}`, borderRadius: '3px', color: (prefs.break_timer || 'on') === o.val ? '#d4d4d4' : '#555', fontSize: '13px', padding: '7px 14px', cursor: 'pointer', fontFamily: 'Georgia, serif' }}>
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div style={{ color: '#777', fontSize: '12px', letterSpacing: '0.1em', marginBottom: '12px' }}>Show last session on home screen</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[{ val: 'show', label: 'Show' }, { val: 'hide', label: 'Hide' }].map(o => {
                    const prefs = userRecord?.preferences || {};
                    return (
                      <button key={o.val} onClick={() => savePreferences({ return_condition: o.val })}
                        style={{ background: 'none', border: `1px solid ${(prefs.return_condition || 'show') === o.val ? '#888' : '#333'}`, borderRadius: '3px', color: (prefs.return_condition || 'show') === o.val ? '#d4d4d4' : '#555', fontSize: '13px', padding: '7px 14px', cursor: 'pointer', fontFamily: 'Georgia, serif' }}>
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {settingsSaving && <div style={{ color: '#555', fontSize: '12px', letterSpacing: '0.08em' }}>saving...</div>}
            </div>
          )}

          {settingsTab === 'privacy' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {[
                { label: 'Clear portrait data', sub: 'Resets everything your AI has learned about you', fn: clearPortrait },
                { label: 'Clear saved ideas', sub: 'Removes all ideas from your lightbulb', fn: clearIdeas },
                { label: 'Clear session history', sub: 'Deletes all past sessions and messages', fn: clearHistory },
                { label: 'Export your data', sub: 'Downloads your full history as JSON', fn: exportData, safe: true }
              ].map(({ label, sub, fn, safe }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '20px', borderBottom: '1px solid #222' }}>
                  <div>
                    <div style={{ color: '#d4d4d4', fontSize: '14px', marginBottom: '3px' }}>{label}</div>
                    <div style={{ color: '#555', fontSize: '12px' }}>{sub}</div>
                  </div>
                  <button
                    disabled={!!clearing}
                    onClick={async () => { if (clearing) return; setClearing(label); try { await fn(); } finally { setClearing(null); } }}
                    style={{ background: 'none', border: `1px solid ${safe ? '#333' : '#3a1a1a'}`, borderRadius: '3px', color: safe ? '#888' : '#a05050', fontSize: '12px', letterSpacing: '0.08em', cursor: clearing ? 'default' : 'pointer', fontFamily: 'Georgia, serif', padding: '7px 14px', flexShrink: 0, marginLeft: '16px', opacity: clearing === label ? 0.5 : 1 }}
                  >
                    {clearing === label ? 'Clearing...' : (safe ? 'export' : 'clear')}
                  </button>
                </div>
              ))}
            </div>
          )}

          {settingsTab === 'account' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ paddingBottom: '20px', borderBottom: '1px solid #222' }}>
                <div style={{ color: '#555', fontSize: '12px', letterSpacing: '0.08em', marginBottom: '6px' }}>signed in as</div>
                <div style={{ color: '#aaa', fontSize: '14px' }}>{session?.user?.email}</div>
              </div>
              <div style={{ paddingBottom: '20px', borderBottom: '1px solid #222' }}>
                <div style={{ color: '#d4d4d4', fontSize: '14px', marginBottom: '12px' }}>Your name</div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#555', fontSize: '13px', letterSpacing: '0.06em', marginBottom: '6px' }}>first</div>
                    <input
                      value={settingsFirstName}
                      onChange={e => setSettingsFirstName(e.target.value)}
                      style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid #333', outline: 'none', color: '#d4d4d4', fontSize: '14px', fontFamily: 'Georgia, serif', padding: '4px 0', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#555', fontSize: '13px', letterSpacing: '0.06em', marginBottom: '6px' }}>last</div>
                    <input
                      value={settingsLastName}
                      onChange={e => setSettingsLastName(e.target.value)}
                      style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid #333', outline: 'none', color: '#d4d4d4', fontSize: '14px', fontFamily: 'Georgia, serif', padding: '4px 0', boxSizing: 'border-box' }}
                    />
                  </div>
                  <button onClick={async () => {
                    await supabase.from('users').update({ first_name: settingsFirstName.trim(), last_name: settingsLastName.trim() }).eq('id', session.user.id);
                    setUserRecord(prev => ({ ...prev, first_name: settingsFirstName.trim(), last_name: settingsLastName.trim() }));
                  }} style={{ background: 'none', border: '1px solid #333', borderRadius: '3px', color: '#888', fontSize: '12px', letterSpacing: '0.08em', cursor: 'pointer', fontFamily: 'Georgia, serif', padding: '7px 14px', alignSelf: 'flex-end', flexShrink: 0 }}>save</button>
                </div>
              </div>
              <div style={{ paddingBottom: '20px', borderBottom: '1px solid #222' }}>
                <div style={{ color: '#d4d4d4', fontSize: '14px', marginBottom: '4px' }}>Username</div>
                <div style={{ color: '#555', fontSize: '12px', marginBottom: '12px', lineHeight: '1.5' }}>How you appear in Spaces and communities. Changes are limited to once every 30 days.</div>
                {(() => {
                  const lastChange = userRecord?.last_username_change;
                  const daysSince = lastChange ? Math.floor((Date.now() - new Date(lastChange).getTime()) / (1000 * 60 * 60 * 24)) : 999;
                  const locked = daysSince < 30;
                  const daysLeft = 30 - daysSince;
                  const nextDate = lastChange ? new Date(new Date(lastChange).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
                  return (
                    <>
                      {locked ? (
                        <div style={{ background: '#111', border: '1px solid #222', borderRadius: '6px', padding: '12px 14px' }}>
                          <div style={{ color: '#555', fontSize: '12px', fontFamily: 'Georgia, serif', lineHeight: 1.6 }}>
                            Currently: <span style={{ color: '#4db8b8' }}>@{userRecord.username}</span>
                          </div>
                          <div style={{ color: '#444', fontSize: '11px', marginTop: '6px', letterSpacing: '0.04em' }}>
                            Next change available {nextDate} · {daysLeft} day{daysLeft === 1 ? '' : 's'} remaining
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                            <div style={{ flex: 1 }}>
                              <input
                                value={usernameInput}
                                onChange={e => setUsernameInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') saveUsernameFromSettings(); }}
                                placeholder={userRecord?.username || 'choose a username'}
                                maxLength={30}
                                style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid #333', outline: 'none', color: '#d4d4d4', fontSize: '14px', fontFamily: 'Georgia, serif', padding: '4px 0', boxSizing: 'border-box' }}
                              />
                            </div>
                            <button onClick={saveUsernameFromSettings}
                              style={{ background: 'none', border: '1px solid #333', borderRadius: '3px', color: '#888', fontSize: '12px', letterSpacing: '0.08em', cursor: 'pointer', fontFamily: 'Georgia, serif', padding: '7px 14px', flexShrink: 0 }}>save</button>
                          </div>
                          {userRecord?.username && (
                            <div style={{ color: '#444', fontSize: '11px', marginTop: '8px' }}>Currently: @{userRecord.username}</div>
                          )}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '20px', borderBottom: '1px solid #222' }}>
                <div style={{ color: '#d4d4d4', fontSize: '14px' }}>Sign out</div>
                <button onClick={() => supabase.auth.signOut()} style={{ background: 'none', border: '1px solid #333', borderRadius: '3px', color: '#888', fontSize: '12px', letterSpacing: '0.08em', cursor: 'pointer', fontFamily: 'Georgia, serif', padding: '7px 14px' }}>sign out</button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ color: '#444', fontSize: '13px', fontStyle: 'italic' }}>Need to delete your account? See Danger zone.</div>
                </div>
              </div>
            </div>
          )}

          {settingsTab === 'danger' && (
            <DangerZone deleteAccount={deleteAccount} />
          )}

      </div>
    </div>
  );
}
